import React from 'react';
import {
  AppBar, Toolbar, Typography, IconButton, Menu, MenuItem, Tooltip,
  useMediaQuery, useTheme as useMuiTheme, Avatar, Box, Chip, Divider, Badge
} from '@mui/material';
import {
  Logout, Person, DarkMode, LightMode, Menu as MenuIcon,
  Notifications, Settings, Business, LocationOn, Home
} from '@mui/icons-material';
import { logout } from '../redux/authSlice';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const HeaderBar = ({ onLogout, onMobileMenuToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { authorizedUser } = useSelector((state) => state.auth);
  const { isDarkMode, toggleTheme } = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = React.useState(null);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleNotificationOpen = (event) => setNotificationAnchorEl(event.currentTarget);
  const handleNotificationClose = () => setNotificationAnchorEl(null);

  const handleLogout = () => {
    // add logout logic here
    console.log('Logout clicked');
    dispatch(logout());
    navigate("/", { replace: true });
  };

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: 1201,
        height: 72,
        backgroundColor: (theme) => theme.palette.mode === 'dark'
          ? 'rgba(30, 30, 30, 0.85)'
          : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
      }}
    >
      <Toolbar sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: '72px !important',
        px: { xs: 2, sm: 3 },
        gap: 1
      }}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <Tooltip title="Open Menu">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onMobileMenuToggle}
              sx={{
                mr: 1,
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.08)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        )}
        
        {/* Logo/Brand Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <IconButton
            onClick={handleHomeClick}
            sx={{
              mr: 1,
              color: 'primary.main',
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(33, 150, 243, 0.1)'
                : 'rgba(33, 150, 243, 0.08)',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(33, 150, 243, 0.15)'
                  : 'rgba(33, 150, 243, 0.12)'
              }
            }}
          >
            <Home />
          </IconButton>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: isMobile ? '1rem' : '1.25rem',
                fontWeight: 700,
                color: 'primary.main',
                lineHeight: 1.2,
                letterSpacing: '0.5px'
              }}
            >
              Co-Op Connect
            </Typography>
            {!isMobile && authorizedUser?.societyName && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <LocationOn sx={{ fontSize: '0.875rem', mr: 0.5, color: 'text.secondary' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.75rem'
                  }}
                >
                  {authorizedUser.societyName} ({authorizedUser.branchCode})
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* User Info Section */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Chip
              avatar={<Avatar sx={{
                bgcolor: 'primary.main',
                width: 24,
                height: 24,
                fontSize: '0.75rem'
              }}>
                {authorizedUser?.displayName?.charAt(0) || 'U'}
              </Avatar>}
              label={authorizedUser?.displayName || 'User'}
              variant="outlined"
              sx={{
                color: 'text.primary',
                borderColor: 'divider',
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
                '& .MuiChip-label': {
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              onClick={handleNotificationOpen}
              sx={{
                color: 'text.primary',
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.08)'
                }
              }}
              size="small"
            >
              <Badge badgeContent={3} color="error">
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Theme Toggle */}
          <Tooltip title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            <IconButton
              onClick={toggleTheme}
              sx={{
                color: 'text.primary',
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.08)'
                }
              }}
              size="small"
            >
              {isDarkMode ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <Tooltip title="Account Menu">
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                color: 'text.primary',
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.08)'
                }
              }}
              size="small"
            >
              {isMobile ? (
                <Avatar sx={{
                  width: 28,
                  height: 28,
                  fontSize: '0.875rem',
                  bgcolor: 'primary.main'
                }}>
                  {authorizedUser?.displayName?.charAt(0) || 'U'}
                </Avatar>
              ) : (
                <Settings fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Enhanced User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: 2,
              boxShadow: (theme) => theme.shadows[8],
              border: (theme) => `1px solid ${theme.palette.divider}`,
              '& .MuiMenuItem-root': {
                borderRadius: 1,
                mx: 0.5,
                my: 0.25,
              }
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* User Info in Menu */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ width: 40, height: 40, mr: 1.5, bgcolor: 'primary.main' }}>
                {authorizedUser?.displayName?.charAt(0) || 'U'}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {authorizedUser?.displayName || 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {authorizedUser?.email || 'user@example.com'}
                </Typography>
              </Box>
            </Box>
            {authorizedUser?.societyName && (
              <Chip
                size="small"
                label={`${authorizedUser.societyName} (${authorizedUser.branchCode})`}
                sx={{ fontSize: '0.75rem' }}
              />
            )}
          </Box>

          {/* Menu Items */}
          <MenuItem
            onClick={handleMenuClose}
            sx={{
              fontSize: '0.9rem',
              py: 1,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <Person fontSize="small" sx={{ mr: 1.5, color: 'primary.main' }} />
            Profile Settings
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          <MenuItem
            onClick={() => { handleMenuClose(); handleLogout(); }}
            sx={{
              fontSize: '0.9rem',
              py: 1,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(244, 67, 54, 0.08)'
                  : 'rgba(244, 67, 54, 0.04)',
              }
            }}
          >
            <Logout fontSize="small" sx={{ mr: 1.5, color: 'error.main' }} />
            Logout
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 300,
              borderRadius: 2,
              boxShadow: (theme) => theme.shadows[8],
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No new notifications
            </Typography>
          </Box>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default HeaderBar;
